import React, { useState, useEffect, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Box,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  Grid,
  Typography,
  MenuItem,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Divider,
  Chip,
  InputAdornment,
  Alert,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import {
  Search,
  GroupAdd,
  KeyboardDoubleArrowRight,
  KeyboardDoubleArrowLeft,
  KeyboardArrowRight,
  KeyboardArrowLeft,
  Save as SaveIcon,
} from "@mui/icons-material"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1400px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

// Funciones de utilidad para los arreglos del Transfer List
const not = (a, b) => a.filter((value) => b.indexOf(value) === -1)
const intersection = (a, b) => a.filter((value) => b.indexOf(value) !== -1)

const AsignarEncuesta = () => {
  const navigate = useNavigate()
  const { state } = useLocation()
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Rescatamos la cabecera de la encuesta desde la grilla
  const encuesta = state?.encuesta
  const enccodigo = encuesta?.enccodigo

  const [tipoAudiencia, setTipoAudiencia] = useState("CLI")
  const [leftSearch, setLeftSearch] = useState("")
  const [rightSearch, setRightSearch] = useState("")

  // Listas de datos
  const [catalogoGlobal, setCatalogoGlobal] = useState([]) // Todo el catálogo actual descargado
  const [asignados, setAsignados] = useState([]) // Panel derecho (ya formateados con audtipo, etc)
  const [checked, setChecked] = useState([]) // Elementos seleccionados en ambas listas

  // ⭐ 1. QUERY: OBTENER ASIGNACIONES EXISTENTES
  const { isLoading: isLoadingAsig, refetch: refetchAsig } = useQuery({
    queryKey: ["getAsignacionesEncuesta", enccodigo],
    queryFn: async () => {
      if (!enccodigo) return null
      const res = await api.post("/AsigEncuestas/getAsignacionesEncuesta", { enccodigo })
      const payload = res?.success !== undefined ? res : res?.data
      if (payload && payload.success) {
        setAsignados(payload.data || [])
      }
      return payload
    },
    enabled: !!enccodigo,
    refetchOnWindowFocus: false,
  })

  // ⭐ 2. QUERY: OBTENER CATÁLOGO SEGÚN EL TIPO
  const { isLoading: isLoadingCat } = useQuery({
    queryKey: ["getCatalogoAudiencia", tipoAudiencia],
    queryFn: async () => {
      const res = await api.post("/AsigEncuestas/getCatalogoAudiencia", { tipo: tipoAudiencia })
      const payload = res?.success !== undefined ? res : res?.data
      if (payload && payload.success) {
        setCatalogoGlobal(payload.data || [])
      }
      return payload
    },
    refetchOnWindowFocus: false,
  })

  // ⭐ 3. MUTATION: GUARDAR ASIGNACIONES
  const { mutateAsync: saveAsignaciones, isPending: isSaving } = useMutation({
    queryKey: ["isSavingAsignacionEncuesta"],
    fn: async (payload) => {
      // Retorno intacto para que la magia SIAC funcione
      return await api.post("/AsigEncuestas/saveAsignacionEncuesta", payload)
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  // ================= LÓGICA DEL TRANSFER LIST =================

  // Filtramos el panel izquierdo: Quitamos los que ya están en el panel derecho y aplicamos el buscador
  const leftList = catalogoGlobal.filter((catItem) => {
    const yaAsignado = asignados.some((asig) => asig.audcodigo === catItem.codigo && asig.audtipo === tipoAudiencia)
    if (yaAsignado) return false
    if (leftSearch) {
      const termino = leftSearch.toLowerCase()
      return catItem.nombre.toLowerCase().includes(termino) || catItem.codigo.toLowerCase().includes(termino)
    }
    return true
  })

  // Filtramos el panel derecho: Solo aplicamos el buscador (Muestra todos los tipos de audiencia mezclados)
  const rightList = asignados.filter((asigItem) => {
    if (rightSearch) {
      const termino = rightSearch.toLowerCase()
      return asigItem.audnombre.toLowerCase().includes(termino) || asigItem.audcodigo.toLowerCase().includes(termino)
    }
    return true
  })

  const leftChecked = intersection(checked, leftList)
  const rightChecked = intersection(checked, rightList)

  const handleToggle = (value) => () => {
    // Si está en la lista derecha y ya fue respondida, bloqueamos el toggle
    if (value.audestado === "R") return

    const currentIndex = checked.indexOf(value)
    const newChecked = [...checked]

    if (currentIndex === -1) {
      newChecked.push(value)
    } else {
      newChecked.splice(currentIndex, 1)
    }
    setChecked(newChecked)
  }

  const handleCheckedRight = () => {
    // Mapeamos del formato Catálogo al formato Audiencia
    const nuevosAsignados = leftChecked.map((item) => ({
      audtipo: tipoAudiencia,
      audcodigo: item.codigo,
      audcedula: item.cedula,
      audnombre: item.nombre,
      audestado: "P", // Pendiente
    }))
    setAsignados((prev) => [...prev, ...nuevosAsignados])
    setChecked(not(checked, leftChecked))
  }

  const handleCheckedLeft = () => {
    // Quitamos de asignados
    setAsignados(not(asignados, rightChecked))
    setChecked(not(checked, rightChecked))
  }

  // ================= GUARDADO FINAL =================
  const handleSubmit = async (e) => {
    if (e) e.preventDefault()
    if (!enccodigo) return showWarning("No se encontró el código de la encuesta.")

    await saveAsignaciones({
      enccodigo,
      asignaciones: asignados,
    })
  }

  // ================= BARRA DE ACCIONES =================
  const ejecutarAction = selectedMenuInfo?.data?.barraAcciones?.find(
    (action) => action?.acccaption === "GRABAR" || action?.acccaption === "EJECUTAR",
  )
  const toolbarActions = ejecutarAction
    ? [
        {
          label: ejecutarAction.acccaption,
          key: ejecutarAction.acccaption,
          icon: getIconComponent(ejecutarAction.accnameicono, ejecutarAction.acctipoico),
        },
      ]
    : [{ label: "GRABAR", key: "GRABAR", icon: <SaveIcon /> }]

  // Componente de UI para cada lista
  const customList = (title, items, isRightSide) => (
    <Paper elevation={2} sx={{ width: "100%", height: 500, overflow: "auto", borderRadius: 2 }}>
      <Box
        sx={{
          p: 2,
          bgcolor: isRightSide ? "#e8f5e9" : "#f5f5f5",
          borderBottom: "1px solid #ddd",
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
          {title} ({items.length})
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar por código o nombre..."
          value={isRightSide ? rightSearch : leftSearch}
          onChange={(e) => (isRightSide ? setRightSearch(e.target.value) : setLeftSearch(e.target.value))}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ bgcolor: "white", borderRadius: 1 }}
        />
      </Box>
      <List dense component="div" role="list">
        {items.map((item, index) => {
          const id = isRightSide ? `${item.audtipo}-${item.audcodigo}` : item.codigo
          const nombre = isRightSide ? item.audnombre : item.nombre
          const codigo = isRightSide ? item.audcodigo : item.codigo
          const estado = isRightSide ? item.audestado : null
          const bloqueado = estado === "R"

          return (
            <ListItem
              key={id}
              role="listitem"
              button
              onClick={handleToggle(item)}
              sx={{ borderBottom: "1px solid #f0f0f0", opacity: bloqueado ? 0.6 : 1 }}
            >
              <ListItemIcon>
                <Checkbox
                  checked={checked.indexOf(item) !== -1 || bloqueado}
                  tabIndex={-1}
                  disableRipple
                  disabled={bloqueado}
                  color={isRightSide ? "success" : "primary"}
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight="500">
                    {nombre}
                  </Typography>
                }
                secondary={`Cód: ${codigo} ${isRightSide ? `| Tipo: ${item.audtipo}` : ""}`}
              />
              {estado === "R" && (
                <Chip
                  label="Respondida"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ ml: 1, fontSize: "0.6rem" }}
                />
              )}
              {estado === "P" && (
                <Chip
                  label="Pendiente"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ ml: 1, fontSize: "0.6rem" }}
                />
              )}
            </ListItem>
          )
        })}
        {items.length === 0 && (
          <Box p={4} textAlign="center">
            <Typography variant="body2" color="textSecondary">
              No hay registros para mostrar
            </Typography>
          </Box>
        )}
      </List>
    </Paper>
  )

  if (!encuesta) {
    return (
      <ThemeProvider theme={theme}>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <BackIcon />
          <Alert severity="error" sx={{ mt: 3 }}>
            No se recibieron los datos de la encuesta. Por favor, regrese a la bandeja principal.
          </Alert>
        </div>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        {/* TOOLBAR */}
        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <span>
                <IconButton
                  onClick={handleSubmit}
                  disabled={isSaving || isLoadingCat || isLoadingAsig}
                  sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "white" }}
                >
                  {action.icon}
                </IconButton>
              </span>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 20px 30px",
            fontSize: "25px",
            color: "#196C87",
          }}
        >
          <GroupAdd sx={{ fontSize: 32, mr: 1 }} />
          <b>Asignación de Audiencia: {enccodigo || ""}</b>
        </div>

        <CustomBackdrop isLoading={isSaving || isLoadingCat || isLoadingAsig} />

        <Box sx={StyledRoot}>
          {/* CABECERA RESUMEN */}
          <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2, borderLeft: "5px solid #196C87", bgcolor: "white" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Typography variant="caption" color="textSecondary">
                  Título de la Encuesta
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="primary">
                  {encuesta.enctitulo}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="textSecondary">
                  Tipo / Zona
                </Typography>
                <Typography variant="body2">
                  <Chip label={encuesta.enctipo} size="small" sx={{ mr: 1 }} />
                  {encuesta.zoncodigo ? `Zona: ${encuesta.zoncodigo}` : "Aplicación Global"}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* TRANSFER LIST DUAL */}
          <Grid container spacing={2} justifyContent="center" alignItems="center">
            {/* PANEL IZQUIERDO */}
            <Grid item xs={12} md={5}>
              <Box mb={2}>
                <TextField
                  select
                  fullWidth
                  label="Origen del Catálogo *"
                  size="small"
                  value={tipoAudiencia}
                  onChange={(e) => {
                    setTipoAudiencia(e.target.value)
                    setChecked([]) // Limpiar selecciones al cambiar de catálogo
                    setLeftSearch("")
                  }}
                  sx={{ bgcolor: "white" }}
                >
                  <MenuItem value="CLI">Clientes (cxcmcli)</MenuItem>
                  <MenuItem value="PRO">Proveedores (cxpmprov)</MenuItem>
                  <MenuItem value="USR">Usuarios de Sistema (siaccusr)</MenuItem>
                </TextField>
              </Box>
              {customList(`Disponibles (${tipoAudiencia})`, leftList, false)}
            </Grid>

            {/* BOTONES DE TRANSFERENCIA */}
            <Grid item xs={12} md={2}>
              <Grid container direction="column" alignItems="center" sx={{ gap: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleCheckedRight}
                  disabled={leftChecked.length === 0}
                  aria-label="move selected right"
                  sx={{ minWidth: 100, bgcolor: "#196C87" }}
                  endIcon={<KeyboardArrowRight />}
                >
                  ASIGNAR
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCheckedLeft}
                  disabled={rightChecked.length === 0}
                  aria-label="move selected left"
                  sx={{ minWidth: 100 }}
                  startIcon={<KeyboardArrowLeft />}
                >
                  QUITAR
                </Button>
                <Divider sx={{ width: "80%", my: 1 }} />
                <Typography variant="caption" color="textSecondary" align="center">
                  Seleccione elementos y use los botones para moverlos.
                </Typography>
              </Grid>
            </Grid>

            {/* PANEL DERECHO */}
            <Grid item xs={12} md={5}>
              <Box mb={2} display="flex" alignItems="center" height={40}>
                <Typography variant="subtitle2" color="secondary" fontWeight="bold">
                  Audiencia Consolidada Final
                </Typography>
              </Box>
              {customList("Listado de Asignados", rightList, true)}
            </Grid>
          </Grid>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default AsignarEncuesta
